import { h } from '@logicflow/core';
import { RectResize } from '@logicflow/extension/es/NodeResize';
import { renderMarkers } from './markers';
import { renderAiChip } from './aiChip';
import { getThemeStyle } from '../theme';
import { renderWrappedText } from './textUtils';

const { view: RectResizeView, model: RectResizeModel } = RectResize;

/** Collapsed dimensions */
const COLLAPSED_WIDTH = 100;
const COLLAPSED_HEIGHT = 80;

class SubProcessView extends RectResizeView {
  getResizeShape() {
    const { model } = this.props;
    const { x, y, width, height, radius } = model;
    const style = model.getNodeStyle();
    const props = model.properties || {};
    const isExpanded = props.isExpanded !== false;

    // Event Sub-Process: dashed border
    const strokeDasharray = props.triggeredByEvent ? '6,3' : undefined;

    const markerSign = isExpanded ? '+' : '+';
    const markerBoxY = y + height / 2 - 16;

    const children = [
      h('rect', {
        x: x - width / 2,
        y: y - height / 2,
        width,
        height,
        rx: radius,
        ry: radius,
        ...style,
        ...(strokeDasharray ? { strokeDasharray } : {}),
      }),
      // BPMN SubProcess marker: [+]/[-] at bottom center
      h('rect', {
        x: x - 7,
        y: markerBoxY,
        width: 14,
        height: 14,
        rx: 1,
        ry: 1,
        fill: 'none',
        stroke: style.stroke,
        strokeWidth: 1,
      }),
      // + or - inside the box
      h('path', {
        d: isExpanded
          ? `M ${x} ${markerBoxY + 2} L ${x} ${markerBoxY + 12} M ${x - 5} ${markerBoxY + 7} L ${x + 5} ${markerBoxY + 7}`
          : `M ${x - 5} ${markerBoxY + 7} L ${x + 5} ${markerBoxY + 7}`,
        stroke: style.stroke,
        strokeWidth: 1.2,
        fill: 'none',
      }),
      // Invisible click target for toggle
      h('rect', {
        x: x - 10,
        y: markerBoxY - 3,
        width: 20,
        height: 20,
        fill: 'transparent',
        stroke: 'none',
        style: 'cursor: pointer;',
        onClick: () => this._toggleExpand(),
      }),
    ];


    // Wrapped text inside the shape
    const label = (props && props.name) || '';
    if (label) {
      children.push(renderWrappedText(h, label, x, y - 4, width - 20, { graphModel: model.graphModel }));
    }

    // Loop / Multi-Instance markers (rendered left of the [+] marker)
    const markers = renderMarkers(x - 18, y, width, height, props, style.stroke);
    children.push(...markers);

    const chip = renderAiChip(x, y, width, height, props);
    if (chip) children.push(chip);

    return h('g', {}, children);
  }

  _toggleExpand() {
    const { model } = this.props;
    const props = model.properties || {};
    const isExpanded = props.isExpanded !== false;

    if (isExpanded) {
      // Collapse: save current size, shrink to collapsed dimensions
      model.setProperties({
        isExpanded: false,
        _expandedWidth: model.width,
        _expandedHeight: model.height,
      });
      model.width = COLLAPSED_WIDTH;
      model.height = COLLAPSED_HEIGHT;
      model.setProperties({ width: COLLAPSED_WIDTH, height: COLLAPSED_HEIGHT });

      // Hide children
      this._setChildrenVisibility(false);
    } else {
      // Expand: restore saved size
      const ew = props._expandedWidth || 300;
      const eh = props._expandedHeight || 200;
      model.setProperties({ isExpanded: true });
      model.width = ew;
      model.height = eh;
      model.setProperties({ width: ew, height: eh });

      // Show children
      this._setChildrenVisibility(true);
    }
  }

  _setChildrenVisibility(visible) {
    const { model } = this.props;
    const lf = model.graphModel;
    if (!lf || !lf.nodes) return;

    // Find nodes that have this subprocess as containment parent
    // by checking if their center is within our bounds
    const bounds = {
      left: model.x - model.width / 2,
      right: model.x + model.width / 2,
      top: model.y - model.height / 2,
      bottom: model.y + model.height / 2,
    };

    for (const node of lf.nodes) {
      if (node.id === model.id) continue;
      if (node.type === 'epx:Participant' || node.type === 'epx:Lane') continue;
      if (node.x >= bounds.left && node.x <= bounds.right &&
          node.y >= bounds.top && node.y <= bounds.bottom) {
        node.visible = visible;
      }
    }
  }
}

class SubProcessModel extends RectResizeModel {
  initNodeData(data) {
    super.initNodeData(data);
    this.width = data.properties?.width || 140;
    this.height = data.properties?.height || 80;
    this.radius = 5;
    this.minWidth = 100;
    this.minHeight = 60;
    this.maxWidth = 1500;
    this.maxHeight = 1000;
    this.text = { ...this.text, editable: false, draggable: false, value: '' };
  }


  getNodeStyle() {
    const style = super.getNodeStyle();
    const t = getThemeStyle(this.graphModel, 'subProcess');
    style.stroke = t.stroke || '#096dd9';
    style.strokeWidth = t.strokeWidth ?? 2;
    style.fill = t.fill || '#e6f7ff';
    return style;
  }

  getResizeOutlineStyle() {
    return { stroke: '#1890ff', strokeWidth: 1, strokeDasharray: '3,3' };
  }
}

export const SubProcess = {
  type: 'epx:SubProcess',
  view: SubProcessView,
  model: SubProcessModel,
};
